<template>
  <v-app>
    <!-- Sidebar Navigation - Hidden in form detail views -->
    <Sidebar v-if="!isFormDetailView" v-model="drawer" v-model:rail="rail" />

    <!-- Top Navigation Bar -->
    <TopNav
      :rail="rail"
      :is-form-view="isFormDetailView"
      @toggle-drawer="drawer = !drawer"
      @toggle-rail="rail = !rail"
    />

    <!-- Main Content Area -->
    <v-main class="main-content" :class="{ 'main-content--fullwidth': isFormDetailView }">
      <v-container fluid :class="isFormDetailView ? 'pa-0' : 'pa-6'">
        <router-view />
      </v-container>
    </v-main>

    <PActionDialog v-if="shouldShowAccessDialog" v-model="accessDialog.show" :type="accessDialog.type" :user-name="userName" />
  </v-app>
</template>

<script>
import Sidebar from '@/components/layout/Sidebar.vue'
import TopNav from '@/components/layout/TopNav.vue'
import PActionDialog from '@/components/pricing/PActionDialog'
import { ACTION_DIALOG_TYPES } from '@/shared/constants/pricing/data'
import userMixin from '@/mixins/user'
import { mapGetters } from 'vuex'

// Routes where the account-access dialog must stay hidden. Forms has its own complete
// inline access handling (FormsListView.vue) so a blocked user can still reach it and see
// exactly what's wrong; Plans/Billing are where they'd go to actually fix it. Every other
// page (Landing Pages, eSign, etc.) has no such inline check of its own, so the dialog
// must keep blocking those - don't add a route here without confirming it self-guards.
const ACCESS_DIALOG_EXEMPT_ROUTES = ['plans.index', 'billing.index', 'forms.index']

export default {
  name: 'Dash',
  mixins: [userMixin],
  components: {
    Sidebar,
    TopNav,
    PActionDialog,
  },
  data() {
    return {
      drawer: true,
      rail: false,
      accessDialog: {
        show: false,
        type: null
      }
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn']),
    userName() {
      return this.user?.name?.split(' ')[0] || null
    },
    isAccessDialogExemptRoute() {
      return ACCESS_DIALOG_EXEMPT_ROUTES.includes(this.$route.name)
    },
    shouldShowAccessDialog() {
      return !this.isAdmin && !this.isSuperCustomer && !this.isAccessDialogExemptRoute
    },
    accessDialogType() {
      // Priority: Account Expired > Subscription Paused > Trial Ended > Past Due
      if (this.userMixin_hasActiveFreePlan || (this.userMixin_hasActiveFreeTrialPlan && this.userMixin_accountExpired)) {
        return ACTION_DIALOG_TYPES.ACCOUNT_EXPIRED
      }
      if (this.userMixin_hasPausedSubscription && !this.userMixin_hasPausedSubscriptionInGrace) {
        return ACTION_DIALOG_TYPES.SUBSCRIPTION_PAUSED
      }
      if (this.userMixin_trialEnded) {
        return ACTION_DIALOG_TYPES.TRIAL_ENDED
      }
      if (this.userMixin_isPastDueUser) {
        return ACTION_DIALOG_TYPES.PAST_DUE
      }
      return null
    },
    isFormDetailView() {
      // Check if we're in any form detail route
      const formDetailRoutes = [
        'forms.overview',
        'forms.leads',
        'forms.detail.connectionstab.list',
        'forms.detail.connectionstab.create',
        'forms.detail.connectionstab.edit',
        'forms.settings',
        'forms.urlBuilder',
        'forms.trackingEvents',
        'forms.variants.create',
        'forms.variants.edit',
        'forms.experiments.overview',
        'forms.experiments.detail',
      ]
      return formDetailRoutes.includes(this.$route.name)
    }
  },
  watch: {
    isLoggedIn: {
      immediate: true,
      handler(isLoggedIn) {
        if (!isLoggedIn) {
          this.$router.replace({ name: 'login' })
        }
      }
    },
    accessDialogType: {
      immediate: true,
      handler(type) {
        this.accessDialog.type = type
        this.accessDialog.show = !!type
      }
    }
  }
}
</script>

<style scoped>
.main-content {
  background-color: var(--background) !important;
}

.main-content--fullwidth {
  padding-left: 0 !important;
}

/* Slim scrollbars with primary color theming */
:deep(.v-main),
:deep(.v-main__wrap),
:deep(.v-navigation-drawer),
:deep(.v-navigation-drawer__content) {
  scrollbar-width: thin;
  scrollbar-color: rgba(var(--brand-color), 0.55) rgba(var(--brand-color), 0.08);
}

:deep(.v-main)::-webkit-scrollbar,
:deep(.v-main__wrap)::-webkit-scrollbar,
:deep(.v-navigation-drawer)::-webkit-scrollbar,
:deep(.v-navigation-drawer__content)::-webkit-scrollbar {
  width: 6px;
}

:deep(.v-main)::-webkit-scrollbar-track,
:deep(.v-main__wrap)::-webkit-scrollbar-track,
:deep(.v-navigation-drawer)::-webkit-scrollbar-track,
:deep(.v-navigation-drawer__content)::-webkit-scrollbar-track {
  background: rgba(var(--brand-color), 0.08);
}

:deep(.v-main)::-webkit-scrollbar-thumb,
:deep(.v-main__wrap)::-webkit-scrollbar-thumb,
:deep(.v-navigation-drawer)::-webkit-scrollbar-thumb,
:deep(.v-navigation-drawer__content)::-webkit-scrollbar-thumb {
  background-color: rgba(var(--brand-color), 0.55);
  border-radius: 999px;
}

:deep(.v-main)::-webkit-scrollbar-thumb:hover,
:deep(.v-main__wrap)::-webkit-scrollbar-thumb:hover,
:deep(.v-navigation-drawer)::-webkit-scrollbar-thumb:hover,
:deep(.v-navigation-drawer__content)::-webkit-scrollbar-thumb:hover {
  background-color: rgba(var(--brand-color), 0.7);
}
</style>
